import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Package, FileUp, Archive, ArchiveRestore, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { InlineItemCode } from "@/components/ItemCode";
import { can } from "@/lib/perms";
import { resolveImageUrl } from "@/lib/imageUrl";

const PLACEHOLDER_IMG = "https://images.unsplash.com/photo-1717386255773-1e3037c81788?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwZXF1aXBtZW50fGVufDB8fHx8MTc4NzEyMjgwMHww&ixlib=rb-4.1.0&q=85";

export default function Catalog() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ model_name: "", item_code: "", manufacturer: "", category: "", list_price: "", cost_price: "", image_url: "", amc_interval_days: 90 });
  const [missingOnly, setMissingOnly] = useState(false);
  const [specFile, setSpecFile] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const IMG_LIMIT_MB = 5;

  const onImageFile = (f) => {
    if (!f) { setImageFile(null); return; }
    if (!f.type.startsWith("image/")) { toast.error("Please choose a JPG, PNG or WEBP image"); return; }
    if (f.size > IMG_LIMIT_MB * 1024 * 1024) {
      toast.error(`Image is ${(f.size / (1024 * 1024)).toFixed(1)} MB — max ${IMG_LIMIT_MB} MB. Try a smaller file.`);
      return;
    }
    setImageFile(f);
  };

  const load = async (q = "", archived = false) => {
    try {
      const params = new URLSearchParams();
      if (q) params.set("search", q);
      if (missingOnly) params.set("missing_code", "true");
      if (archived) {
        const res = await api.get("/admin/archived/catalog");
        setItems(res.data);
        return;
      }
      const res = await api.get(`/catalog?${params.toString()}`);
      setItems(res.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load("", showArchived); }, [showArchived]);
  useEffect(() => { if (!showArchived) { const t = setTimeout(() => load(search, false), 300); return () => clearTimeout(t); } }, [search, showArchived, missingOnly]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        list_price: parseFloat(form.list_price) || 0,
        amc_interval_days: parseInt(form.amc_interval_days) || 90,
      };
      if (isAdmin) payload.cost_price = parseFloat(form.cost_price) || 0;
      else { delete payload.cost_price; delete payload.item_code; }
      const res = await api.post("/catalog", payload);
      let created = res.data;
      if (imageFile) {
        const fd = new FormData();
        fd.append("file", imageFile);
        try {
          const up = await api.post(`/catalog/${created.id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          created = up.data;
        } catch (upErr) {
          toast.error(`Model saved, but image upload failed: ${formatApiError(upErr)}`);
        }
      }
      if (specFile) {
        const fd = new FormData();
        fd.append("file", specFile);
        try {
          const up = await api.post(`/catalog/${created.id}/spec-sheet`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          created = up.data;
        } catch (upErr) {
          toast.error(`Model saved, but PDF upload failed: ${formatApiError(upErr)}`);
        }
      }
      setItems((p) => [created, ...p]);
      toast.success("Model added to catalog");
      setOpen(false);
      setForm({ model_name: "", item_code: "", manufacturer: "", category: "", list_price: "", cost_price: "", image_url: "", amc_interval_days: 90 });
      setSpecFile(null);
      setImageFile(null);
    } catch (e2) { toast.error(formatApiError(e2)); } finally { setSaving(false); }
  };

  const restore = async (id, label) => {
    if (!window.confirm(`Restore "${label}" from the archive?`)) return;
    try {
      await api.post(`/admin/restore/catalog/${id}`);
      toast.success("Model restored");
      load("", showArchived);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="catalog-page">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Products</div>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold text-slate-900">Machine catalog</h1>
          <p className="text-slate-500 text-sm mt-1 hidden md:block">Master list of machine models — specs, pricing, AMC intervals.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          {can(user, "catalog", "edit") && <DialogTrigger asChild>
            <Button className="bg-indigo-600 hover:bg-indigo-700 w-full md:w-auto" data-testid="new-catalog-btn"><Plus className="w-4 h-4 mr-1.5" /> Add model</Button>
          </DialogTrigger>}
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Add machine model</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Model name *</Label>
                  <Input required value={form.model_name} onChange={(e) => setForm({ ...form, model_name: e.target.value })} data-testid="catalog-model-input" className="mt-1" />
                </div>
                {isAdmin && (
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Item code</Label>
                    <Input value={form.item_code} onChange={(e) => setForm({ ...form, item_code: e.target.value.toUpperCase() })} placeholder="e.g. HFT-R5-STD" data-testid="catalog-item-code-input" className="mt-1 font-mono uppercase placeholder:normal-case" />
                  </div>
                )}
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Manufacturer *</Label>
                  <Input required value={form.manufacturer} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} data-testid="catalog-mfr-input" className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Category *</Label>
                  <Input required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="catalog-category-input" className="mt-1" placeholder="e.g. CNC Lathe" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">List price (INR) *</Label>
                  <Input required type="number" step="0.01" value={form.list_price} onChange={(e) => setForm({ ...form, list_price: e.target.value })} data-testid="catalog-price-input" className="mt-1" />
                </div>
                {isAdmin && (
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-rose-600">Cost price (INR) · Admin only</Label>
                    <Input
                      type="number" step="0.01"
                      value={form.cost_price}
                      onChange={(e) => setForm({ ...form, cost_price: e.target.value })}
                      data-testid="catalog-cost-price-input"
                      className="mt-1 border-rose-200 focus:border-rose-400"
                      placeholder="Internal landed cost"
                    />
                    <div className="text-[10px] text-slate-400 mt-1">Not visible to Sales Reps or Managers.</div>
                  </div>
                )}
                <div className="col-span-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Machine image</Label>
                  <div className="mt-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <Input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        capture="environment"
                        onChange={(e) => onImageFile(e.target.files?.[0] || null)}
                        data-testid="catalog-image-file-input"
                        className="file:mr-3 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-indigo-50 file:text-indigo-700"
                      />
                    </div>
                    {imageFile && (
                      <div className="text-xs text-emerald-700 bg-emerald-50 rounded px-2 py-1">
                        Selected: {imageFile.name} ({(imageFile.size / 1024).toFixed(0)} KB)
                      </div>
                    )}
                    <div className="text-[11px] text-slate-500">
                      Upload from your device — JPG, PNG, WEBP up to {IMG_LIMIT_MB} MB. On mobile you can also take a fresh photo. Or paste a public URL below.
                    </div>
                    <Input
                      value={form.image_url}
                      onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                      data-testid="catalog-image-input"
                      placeholder="https://…  (optional, ignored if a file is uploaded)"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">AMC interval (days)</Label>
                  <Input type="number" value={form.amc_interval_days} onChange={(e) => setForm({ ...form, amc_interval_days: e.target.value })} data-testid="catalog-amc-input" className="mt-1" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Spec sheet (PDF, max 20 MB)</Label>
                  <div className="mt-1 flex items-center gap-2">
                    <Input
                      type="file"
                      accept="application/pdf"
                      onChange={(e) => setSpecFile(e.target.files?.[0] || null)}
                      data-testid="catalog-spec-file-input"
                      className="file:mr-3 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-indigo-50 file:text-indigo-700"
                    />
                  </div>
                  {specFile && <div className="text-xs text-slate-500 mt-1">Selected: {specFile.name} ({(specFile.size / 1024).toFixed(0)} KB)</div>}
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="catalog-save-btn">{saving ? "Saving..." : "Add model"}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mb-4 flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search models, item code, manufacturer, category..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="catalog-search-input" disabled={showArchived} />
        </div>
        {!showArchived && (
          <Button variant={missingOnly ? "default" : "outline"} size="sm" onClick={() => setMissingOnly(!missingOnly)}
            className={missingOnly ? "bg-amber-600 hover:bg-amber-700" : "text-amber-700 border-amber-200 hover:bg-amber-50"} data-testid="catalog-missing-code-filter">
            <AlertTriangle className="w-4 h-4 mr-1.5" /> Item code missing
          </Button>
        )}
        {isAdmin && (
          <Button
            variant={showArchived ? "default" : "outline"}
            onClick={() => setShowArchived(!showArchived)}
            className={showArchived ? "bg-slate-800 hover:bg-slate-900" : ""}
            data-testid="catalog-toggle-archived"
            size="sm"
          >
            <Archive className="w-4 h-4 mr-1.5" />
            {showArchived ? "Showing archived" : "Show archived"}
          </Button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-16 text-center text-slate-400" data-testid="catalog-empty">
          <Package className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          {showArchived ? "No archived machine models." : "No machine models yet."}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {items.map((m) => (
            showArchived ? (
              <div key={m.id} data-testid={`catalog-archived-${m.id}`} className="bg-slate-50 border border-slate-200 rounded-lg overflow-hidden opacity-90">
                <div className="aspect-[4/3] bg-slate-100 overflow-hidden relative">
                  <img src={resolveImageUrl(m.image_url) || PLACEHOLDER_IMG} alt={m.model_name} className="w-full h-full object-cover grayscale" />
                  <div className="absolute top-2 left-2 bg-slate-800 text-white text-[10px] font-semibold px-2 py-0.5 rounded uppercase tracking-wider">Archived</div>
                </div>
                <div className="p-4">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">{m.category}</div>
                  <div className="font-heading font-semibold text-slate-900 text-base">{m.model_name}</div>
                  <div className="text-sm text-slate-500 mb-2">{m.manufacturer}</div>
                  <div className="text-[11px] text-slate-500 mb-3">Archived {m.deleted_at ? new Date(m.deleted_at).toLocaleString() : ""}</div>
                  <Button size="sm" variant="outline" className="w-full" onClick={() => restore(m.id, m.model_name)} data-testid={`catalog-restore-${m.id}`}>
                    <ArchiveRestore className="w-3.5 h-3.5 mr-1" /> Restore
                  </Button>
                </div>
              </div>
            ) : (
              <Link to={`/catalog/${m.id}`} key={m.id} data-testid={`catalog-card-${m.id}`}
                className="bg-white border border-slate-200 rounded-lg overflow-hidden hover:border-indigo-300 hover:shadow-sm transition-colors duration-150">
                <div className="aspect-[4/3] bg-slate-100 overflow-hidden">
                  <img src={resolveImageUrl(m.image_url) || PLACEHOLDER_IMG} alt={m.model_name} className="w-full h-full object-cover" />
                </div>
                <div className="p-4">
                  <div className="text-xs font-semibold uppercase tracking-wider text-indigo-600 mb-1">{m.category}</div>
                  <div className="font-heading font-semibold text-slate-900 text-base">{m.model_name}</div>
                  <div className="mt-1"><InlineItemCode model={m} canEdit={isAdmin} onSaved={(d) => setItems((p) => (missingOnly && d.item_code ? p.filter((x) => x.id !== d.id) : p.map((x) => (x.id === d.id ? { ...x, item_code: d.item_code } : x))))} /></div>
                  <div className="text-sm text-slate-500 mb-2 mt-1">{m.manufacturer}</div>
                  <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                    <div className="font-mono text-sm text-slate-900">₹ {Number(m.list_price).toLocaleString("en-IN")}</div>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      {m.spec_sheet_file && <FileUp className="w-3.5 h-3.5 text-indigo-500" title="Spec sheet uploaded" />}
                      <span>AMC {m.amc_interval_days}d</span>
                    </div>
                  </div>
                  {isAdmin && m.cost_price != null && Number(m.cost_price) > 0 && (
                    <div className="mt-2 pt-2 border-t border-dashed border-rose-100 flex justify-between items-center">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-rose-600">Cost · Admin</span>
                      <span className="font-mono text-xs text-rose-700">₹ {Number(m.cost_price).toLocaleString("en-IN")}</span>
                    </div>
                  )}
                </div>
              </Link>
            )
          ))}
        </div>
      )}
    </div>
  );
}
